import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MaximizeIcon, XIcon } from "lucide-react";
import { CheckList } from "./CheckList";
import { insideGallery, visualChecks } from "../data/content";
export function VisualPlans() {
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const page = insideGallery[active];
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setZoomed(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);
  return <section className="bg-peach px-4 py-16" aria-labelledby="visual-title" data-id="element-304" data-mp="1f0t6g0vv0vu2">
      <div className="mx-auto max-w-6xl" data-id="element-305" data-mp="19zlx3xe2jola">
        <div className="text-center" data-id="element-306" data-mp="3jjpbl07efvx">
          <p className="font-heading text-sm font-bold uppercase text-brand" data-id="element-307" data-mp="3kky1itqyqah">Take a look inside</p>
          <h2 id="visual-title" className="mt-2 font-heading text-2xl font-bold uppercase leading-tight md:text-3xl" data-id="element-308" data-mp="3upbccbiopcei">
            See exactly <span className="text-brand" data-id="element-309" data-mp="m54mi83akzsd">what you're building</span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-ink-muted md:text-base" data-id="element-310" data-mp="r5is7jhf7xn8">
            Real pages from Volume 02 — Transformable Builds. Every project comes laid out like this.
          </p>
        </div>

        <div className="mt-12 grid items-start gap-10 lg:grid-cols-[1.15fr_1fr]" data-id="element-311" data-mp="i7q9e5chq7v3">
          <div data-id="element-312" data-mp="3feozd859zwr">
            <button onClick={() => setZoomed(true)} className="group relative block w-full overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" aria-label={`Enlarge ${page.title} page`} data-id="element-313" data-mp="3ewkjtbbwfdr">
              <AnimatePresence mode="wait" initial={false} data-id="element-314" data-mp="3gnj2on5u0j2o">
                <motion.img key={page.src} src={page.src} alt={`Inside page: ${page.title} plan with snapshot, materials, cut list and technical views`} className="w-full" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.18,
                ease: "easeOut"
              }} data-id="element-315" data-mp="3gpcf6j34lxil" />
              </AnimatePresence>
              <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-ink/80 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100" data-id="element-316" data-mp="1dgabqtw3uwnq">
                <MaximizeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-317" data-mp="261a8k4q9nptf" />
                Click to enlarge
              </span>
            </button>

            <ul className="mt-4 grid grid-cols-4 gap-3" role="tablist" aria-label="Inside pages" data-id="element-318" data-mp="1w606xi3s5kg6">
              {insideGallery.map((p, i) => <li key={p.src} data-id="element-319" data-mp="pn217he2hn2d" data-mpi={i}>
                  <button role="tab" aria-selected={i === active} onClick={() => setActive(i)} className={`block w-full overflow-hidden rounded-md border-2 bg-white transition-colors duration-150 ${i === active ? "border-brand" : "border-transparent hover:border-brand/40"}`} data-id="element-320" data-mp="kqbnh0pqj3k4">
                    <img src={p.src} alt="" className="aspect-[4/5] w-full object-cover object-top" data-id="element-321" data-mp="pc2qupa379i2" />
                  </button>
                  <p className={`mt-1.5 text-center text-[11px] font-medium leading-tight ${i === active ? "text-brand" : "text-ink-muted"}`} data-id="element-322" data-mp="tccw1zq21vvv">{p.title}</p>
                </li>)}
            </ul>
          </div>

          <div className="lg:sticky lg:top-8" data-id="element-323" data-mp="138y3erpjrj2v">
            <p className="font-heading text-xs font-bold uppercase text-brand" data-id="element-324" data-mp="188u5gleop0fs">Now viewing</p>
            <h3 className="mt-1 font-heading text-2xl font-bold uppercase" data-id="element-325" data-mp="352x8uwfzn67n">{page.title}</h3>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-326" data-mp="3a0gpg305rv2r">{page.caption}</p>
            <p className="mt-6 text-sm font-medium" data-id="element-327" data-mp="14cy3s5caomf4">Why start cutting before you understand the final project?</p>
            <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-328" data-mp="zeei861m5cna">Depending on the project, every plan shows you:</p>
            <div className="mt-5" data-id="element-329" data-mp="1dcl7ixshy4bc">
              <CheckList items={visualChecks} columns={2} data-id="element-330" data-mp="1dbjwuufe7ju5" />
            </div>
            <p className="mt-6 text-sm leading-relaxed text-ink-muted" data-id="element-331" data-mp="18fuqo88vb4ov">
              So you understand not only what the project looks like, but <strong className="text-ink" data-id="element-332" data-mp="kmmdsp3cxj0c">how it actually goes together.</strong>
            </p>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-333" data-mp="uf1wjomhabma">
        {zoomed && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoomed(false)} role="dialog" aria-modal="true" aria-label={`${page.title} page`} data-id="element-334" data-mp="ug3bw5aj1k9t">
            <motion.img src={page.src} alt={`Inside page: ${page.title}`} className="max-h-full max-w-full rounded-lg bg-white" initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-335" data-mp="2ddzq61gusv8g" />
            <button onClick={() => setZoomed(false)} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-ink" aria-label="Close" data-id="element-336" data-mp="2wa7zphgwfs9k">
              <XIcon className="h-5 w-5" data-id="element-337" data-mp="16wut1llbds5a" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}